import React, { FC, KeyboardEvent } from 'react';

import styled from 'styled-components';

import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import UIButton, { EUIButtonVariant } from '@aquariuxfintech/aqx-trader-react-ui/button';
import UIFormItem, { EUIFormItemVariant } from '@aquariuxfintech/aqx-trader-react-ui/form';
import { ESpacing, ETypography } from '@aquariuxfintech/aqx-trader-react-ui/foundation';
import UIInput, { EUIInputVariant } from '@aquariuxfintech/aqx-trader-react-ui/input';
import UIText from '@aquariuxfintech/aqx-trader-react-ui/text';

import { AUTHENTICATION_ROUTES } from '@aqx-core/common/constants/router';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { ISignUpDemoValues, useSignUpDemoForm } from '@aqx-core/common/hooks/form/use-sign-up-demo-form';
import { useSignUpDemo } from '@aqx-core/common/services/sign-up-demo';
import { getEnterKeycode } from '@aqx-core/common/utils/keyboard';
import CountrySelectField from '@aqx-member/web/screens/auth/sign-up-demo/content/form/country';
import SignUpNameFieldForm from '@aqx-member/web/screens/auth/sign-up-demo/content/form/name-field';

import AuthAcknowledgementText from '../../../shared/acknowledgement-text';
import AuthFormSecondaryAction from '../../../shared/form-secondary-action';
import SignUpDemoDepositAmountField from './deposit-amount';
import PhoneNumberField from './phone-number';

const defaultValues: Partial<ISignUpDemoValues> = {
  agree: true
};

const SignUpDemoForm: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { loading } = useSignUpDemo();
  const {
    useFormMethods,
    submit,
    firstName,
    lastName,
    email,
    dialCode,
    localPhoneNumber,
    depositAmount,
    country,
    isDisabled
  } = useSignUpDemoForm({ defaultValues });

  const { formState, register, setValue, trigger } = useFormMethods;

  const {
    errors: {
      email: emailError,
      dialCode: dialCodeError,
      localPhoneNumber: localPhoneNumberError,
      depositAmount: depositAmountError,
      country: countryError
    }
  } = formState;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (getEnterKeycode(event)) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <StyledContainer>
      <UIText typography={ETypography.DISPLAY_M_BOLD}>{t('Open demo account')}</UIText>

      <FormProvider {...useFormMethods}>
        <StyledFormContainer onSubmit={submit}>
          <StyledFormInputsContainer>
            <SignUpNameFieldForm
              firstName={firstName}
              lastName={lastName}
              submit={submit}
              formState={formState}
              register={register}
            />
            <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} label={t('Email')} size="large">
              <UIInput
                {...register('email')}
                variant={EUIInputVariant.BORDERED_TEXT}
                placeholder={t('Enter your email')}
                value={email}
                error={emailError?.message}
                onKeyDown={handleKeyDown}
                dataTestId={EDataTestId.DEMO_SIGN_UP_EMAIL_INPUT}
                size="large"
              />
            </UIFormItem>
            <CountrySelectField
              onCountryChange={(nextValue: string) => {
                setValue('country', nextValue);
                trigger('country');
              }}
              value={country}
              error={countryError?.message}
            />
            <PhoneNumberField
              dialCode={dialCode}
              localPhoneNumber={localPhoneNumber}
              register={register}
              setValue={setValue}
              trigger={trigger}
              localPhoneNumberError={localPhoneNumberError?.message}
              dialCodeError={dialCodeError?.message}
              handleKeyDown={handleKeyDown}
            />

            <SignUpDemoDepositAmountField
              setValue={setValue}
              trigger={trigger}
              depositAmount={depositAmount}
              error={depositAmountError?.message}
            />
          </StyledFormInputsContainer>
          <AuthAcknowledgementText
            prefixText={t('By selecting "Agree and continue" below, I agree to the')}
            postfixText={t('for opening a demo account.')}
          />
          <StyledButtonContainer>
            <UIButton
              size="large"
              stretch={true}
              loading={loading}
              disabled={isDisabled}
              variant={EUIButtonVariant.FILLED_THEMED}
              dataTestId={EDataTestId.DEMO_SIGN_UP_AGREE_AND_CONTINUE}
            >
              {t('Agree and continue')}
            </UIButton>
          </StyledButtonContainer>
        </StyledFormContainer>
      </FormProvider>
      <AuthFormSecondaryAction
        infoText={t('Already have an account?')}
        actionText={t('Log In')}
        onClickActionText={() => navigate(AUTHENTICATION_ROUTES.LOGIN)}
      />
    </StyledContainer>
  );
};

export default SignUpDemoForm;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 512px;
`;

const StyledFormContainer = styled.form`
  display: flex;
  flex-direction: column;
`;

const StyledFormInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${ESpacing.XL}px;
  margin-top: ${ESpacing.XXXL}px;
  margin-bottom: ${ESpacing.XL}px;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  margin-top: ${ESpacing.XL}px;
  margin-bottom: ${ESpacing.M}px;
`;
